import React, { useEffect, useState } from "react";
import StatusDropdown from "../Shared/StatusDropdown";
import { useNavigate } from "react-router-dom";
import { dropdownTaxes, dropdownParentCategories } from "../../api/dropdownService";
import SeoFields from "../../common/SEOFields";
import { getErrorMessage } from "../../utils/errorHandler";
import FullPageLoader from "../../common/FullPageLoader";

export default function CategoryForm({
  onSubmit,
  loading = false,
  isEdit = false,
  backendErrors = {},
  initialData = {},
}) {
  const navigate = useNavigate();

  const initialState = {
    name: "",
    tagline: "",
    description: "",
    coverImage: "",
    hsnCode: "",
    taxApplied: false,
    taxId: "",
    status: "ACTIVE",
    type: "MAIN",
    parentId: "null",
    // SEO
    metaTitle: "",
    metaKeywords: "",
    metaDescription: "",
    metaImage: "",
    metaImagePreview: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [taxes, setTaxes] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isMetaTitleEdited, setIsMetaTitleEdited] = useState(false);
  const [isMetaDescriptionEdited, setIsMetaDescriptionEdited] = useState(false);
  const [parentCategories, setParentCategories] = useState([]);
  const [parentCategoriesLoading, setParentCategoriesLoading] = useState(true);
  const [parentCategoriesError, setParentCategoriesError] = useState("");

  // =========================
  // AUTO FILL EDIT DATA
  // =========================
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        name: initialData.name || "",
        tagline: initialData.tagline || "",
        description: initialData.description || "",
        hsnCode: initialData.hsnCode || "",
        taxApplied: initialData.taxApplied === true || initialData.taxApplied === "true",
        taxId: initialData.taxId || "",
        status: initialData.status || "ACTIVE",
        type: initialData.type || "MAIN",
        parentId:
          initialData.parentId?._id || initialData.parentId?.id || initialData.parentId || "",

        // SEO
        metaTitle: initialData.metaTitle || "",
        metaKeywords: Array.isArray(initialData.metaKeywords)
          ? initialData.metaKeywords.join(", ")
          : initialData.metaKeywords || "",
        metaDescription: initialData.metaDescription || "",
        metaImage:
          initialData?.metaImage ||
          initialData?.metaImageUrl ||
          initialData?.meta_image ||
          initialData?.meta_image_url ||
          initialData?.seo?.image ||
          initialData?.seo?.metaImage ||
          initialData?.seo?.meta_image ||
          initialData?.seo?.imageUrl ||
          initialData?.seo?.metaImageUrl ||
          "",
        metaImagePreview:
          initialData?.metaImage ||
          initialData?.metaImageUrl ||
          initialData?.meta_image ||
          initialData?.meta_image_url ||
          initialData?.seo?.image ||
          initialData?.seo?.metaImage ||
          initialData?.seo?.meta_image ||
          initialData?.seo?.imageUrl ||
          initialData?.seo?.metaImageUrl ||
          "",
      });
    }
  }, [initialData]);

  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        setParentCategoriesLoading(true);
        setParentCategoriesError("");
        const res = await dropdownParentCategories();

        const data = res?.data?.data || res?.data?.categories || res?.data || [];

        setParentCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        setParentCategories([]);
        setParentCategoriesError(getErrorMessage(error));
      } finally {
        setParentCategoriesLoading(false);
      }
    };

    fetchParentCategories();
  }, []);

  // =========================
  // FETCH TAXES
  // =========================
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const res = await dropdownTaxes();
        setTaxes(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch taxes", error);
      }
    };

    fetchTaxes();
  }, []);

  // =========================
  // HANDLE CHANGE
  // =========================
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // =========================
    // IMAGE TO BASE64
    // =========================
    if (files && files[0]) {
      const file = files[0];

      const reader = new FileReader();

      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [name]: reader.result,

          ...(name === "coverImage" && {
            coverImagePreview: reader.result,
          }),
        }));
      };

      reader.readAsDataURL(file);

      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));

      return;
    }

    // =========================
    // CATEGORY NAME
    // =========================
    if (name === "name") {
      setFormData((prev) => ({
        ...prev,
        name: value,

        // Auto-fill Meta Title
        ...(isMetaTitleEdited
          ? {}
          : {
              metaTitle: value,
            }),
      }));
    }

    // =========================
    // DESCRIPTION
    // =========================
    else if (name === "description") {
      setFormData((prev) => ({
        ...prev,
        description: value,

        // Auto-fill Meta Description
        ...(isMetaDescriptionEdited
          ? {}
          : {
              metaDescription: value,
            }),
      }));
    }

    // =========================
    // META TITLE
    // =========================
    else if (name === "metaTitle") {
      setIsMetaTitleEdited(true);

      setFormData((prev) => ({
        ...prev,
        metaTitle: value,
      }));
    }

    // =========================
    // META DESCRIPTION
    // =========================
    else if (name === "metaDescription") {
      setIsMetaDescriptionEdited(true);

      setFormData((prev) => ({
        ...prev,
        metaDescription: value,
      }));
    }

    // =========================
    // OTHER FIELDS
    // =========================
    else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "type" && value === "MAIN" ? { parentId: "" } : {}),
      }));
    }

    // Clear error
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // =========================
  // HANDLE SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setErrors({});

      const payload = {
        name: formData.name,
        tagline: formData.tagline,
        description: formData.description,
        coverImage: formData.coverImage,
        hsnCode: formData.hsnCode,
        status: formData.status,
        taxApplied: formData.taxApplied,
        // An empty string is not a valid Mongo ObjectId. Omit taxId when tax
        // is disabled or no tax has been selected.
        taxId: formData.taxApplied && formData.taxId ? formData.taxId : undefined,
        type: formData.type,
        parentId: formData.parentId,

        // SEO
        metaTitle: formData.metaTitle,
        metaKeywords: formData.metaKeywords
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        metaDescription: formData.metaDescription,
        metaImage: formData.metaImage,
      };

      const success = await onSubmit(payload);

      if (!success) {
        return;
      }

      setSuccessMessage(isEdit ? "Category Updated Successfully" : "Category Added Successfully");

      setTimeout(() => {
        setSuccessMessage("");
        navigate("/categories");
      }, 1500);
    } catch (error) {
      console.log(error);

      if (error.inner) {
        const validationErrors = {};

        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });

        setErrors(validationErrors);
      }
    }
  };

  return (
    <div className="container-fluid py-3">
      {loading && <FullPageLoader text="Saving..." />}
      <div className="card border-2">
        {successMessage && (
          <div className="px-4 pt-2">
            <div className="alert alert-success mb-0">{successMessage}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card-body p-4">
            <div className="row g-3">
              {/* Category Name */}
              <div className="col-md-12">
                <label className="form-label">
                  Category name <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  name="name"
                  className={`form-control ${
                    errors.name || backendErrors?.name ? "is-invalid" : ""
                  }`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  maxLength={100}
                />

                {(errors.name || backendErrors?.name) && (
                  <div className="invalid-feedback d-block">
                    {errors.name || backendErrors.name}
                  </div>
                )}
              </div>

              {/* Tagline */}
              <div className="col-md-12">
                <label className="form-label">
                 Category tagline 
                </label>

                <input
                  type="text"
                  name="tagline"
                  className={`form-control ${
                    errors.tagline || backendErrors?.tagline ? "is-invalid" : ""
                  }`}
                  value={formData.tagline}
                  onChange={handleChange}
                  placeholder="Enter category tagline"
                  maxLength={100}
                />

                {(errors.tagline || backendErrors?.tagline) && (
                  <div className="invalid-feedback d-block">
                    {errors.tagline || backendErrors.tagline}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="col-md-12 h120">
                <label className="form-label">
                  Category description <span className="text-danger">*</span>
                </label>

                <textarea
                  rows={3}
                  name="description"
                  className={`form-control ${
                    errors.description || backendErrors?.description ? "is-invalid" : ""
                  }`}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter category description"
                  maxLength={160}
                />

                {(errors.description || backendErrors?.description) && (
                  <div className="invalid-feedback d-block">
                    {errors.description || backendErrors.description}
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3">
              {/* HSN Code */}
              <div className="col-md-3 h80">
                <label className="form-label">
                  HSN code<span className="error">*</span>
                </label>

                <input
                  type="text"
                  name="hsnCode"
                  className={`form-control ${
                    errors.hsnCode || backendErrors?.hsnCode ? "is-invalid" : ""
                  }`}
                  value={formData.hsnCode}
                  onChange={handleChange}
                  placeholder="Enter HSN code"
                />
                {(errors.hsnCode || backendErrors?.hsnCode) && (
                  <div className="invalid-feedback d-block">
                    {errors.hsnCode || backendErrors.hsnCode}
                  </div>
                )}
              </div>
              <div className="col-md-3 h80">
                <label className="form-label">Status</label>

                <StatusDropdown
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 h80">
                <label className="form-label">Type</label>

                <select
                  name="type"
                  className={`form-select ${
                    errors.type || backendErrors?.type ? "is-invalid" : ""
                  }`}
                  value={formData.type}
                  onChange={handleChange}
                >
                  <option value="MAIN">MAIN</option>
                  <option value="SUB">SUB</option>
                </select>

                {(errors.type || backendErrors?.type) && (
                  <div className="invalid-feedback d-block">
                    {errors.type || backendErrors.type}
                  </div>
                )}
              </div>

              {formData.type === "SUB" && (
                <div className="col-md-3 h80">
                  <label className="form-label">Parent category<span className="error">*</span> </label>

                  <select
                    name="parentId"
                    className={`form-select ${
                      errors.parentId || backendErrors?.parentId ? "is-invalid" : ""
                    }`}
                    value={formData.parentId}
                    onChange={handleChange}
                    disabled={parentCategoriesLoading}
                  >
                    <option value="">
                      {parentCategoriesLoading
                        ? "Loading parent categories..."
                        : "Select Parent Category"}
                    </option>

                    {parentCategories.map((category) => (
                      <option key={category._id || category.id} value={category._id || category.id}>
                        {category.name ||
                          category.title ||
                          category.label ||
                          category._id ||
                          category.id}
                      </option>
                    ))}
                  </select>

                  {parentCategoriesError && (
                    <div className="text-danger">{parentCategoriesError}</div>
                  )}

                  {(errors.parentId || backendErrors?.parentId) && (
                    <div className="invalid-feedback d-block">
                      {errors.parentId || backendErrors.parentId}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-3 h80">
                <label className="form-label">Tax applied <span className="error">*</span></label>

                <select
                  name="taxApplied"
                  className="form-select"
                  value={formData.taxApplied}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      taxApplied: e.target.value === "true",
                      taxId: e.target.value === "true" ? prev.taxId : "",
                    }))
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              {formData.taxApplied && (
                <div className="col-md-4 h80">
                  <label className="form-label">Tax <span className="error">*</span></label>

                  <select
                    name="taxId"
                    className={`form-select ${backendErrors?.taxId ? "is-invalid" : ""}`}
                    value={formData.taxId}
                    onChange={handleChange}
                  >
                    <option value="">Select Tax</option>

                    {taxes.map((tax) => (
                      <option key={tax._id} value={tax._id}>
                        {tax.taxName}
                      </option>
                    ))}
                  </select>

                  {backendErrors?.taxId && (
                    <div className="invalid-feedback d-block">{backendErrors.taxId}</div>
                  )}
                </div>
              )}
            </div>

            {/* =========================
                SEO INFORMATION
            ========================= */}
            <div className="border-top mt-4 pt-4">
              <SeoFields
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                errors={errors}
                backendErrors={backendErrors}
              />
            </div>
          </div>

          {/* =========================
              FOOTER BUTTONS
          ========================= */}
          <div className="card-footer bg-white border-top p-3">
            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Submitting..." : isEdit ? "Update Category" : "Add Category"}
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: "#032B4D",
                  color: "#fff",
                }}
                onClick={() => navigate("/categories")}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
